import { useState } from 'react';
import moment from 'moment';
import { DotsThreeIcon, FlagIcon, HandsClappingIcon, ThumbsDownIcon } from '@phosphor-icons/react';
import { Avatar, AvatarImage, AvatarFallback } from '../../../components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../../components/ui/dropdown-menu';
import SolutionImage from './SolutionImage';

const OtherSolutionCard = ({ solution, onVote, onReport }) => {
  const [localUpvotes, setLocalUpvotes] = useState(solution.upvotes ?? 0);
  const [myVote, setMyVote] = useState(solution.userVote ?? null);
  const [isVoting, setIsVoting] = useState(false);

  const handleVote = async (vote) => {
    if (isVoting) return;
    setIsVoting(true);
    const wasSameVote = myVote === vote;
    try {
      const result = await onVote(solution._id, vote);
      setLocalUpvotes(result.upvotes);
      setMyVote(wasSameVote ? null : vote);
    } catch (error) {
      // onVote already surfaces a toast on failure — nothing further to do
      // here, just avoid an unhandled promise rejection.
    } finally {
      setIsVoting(false);
    }
  };

  return (
    <div className="py-4 first:pt-0 last:pb-0 flex flex-col gap-3">
      <div className="flex flex-row items-center justify-between">
        <div className="flex flex-row items-center gap-3">
          <Avatar>
            <AvatarImage src={solution.userProfilePicture} alt={solution.userName} />
            <AvatarFallback>{solution.userName?.[0] || 'U'}</AvatarFallback>
          </Avatar>
          <div className="flex flex-col">
            <span className="text-[14px] font-semibold text-[var(--q3-neutral-default)]">{solution.userName}</span>
            <span className="text-[12px] text-[var(--q3-neutral-light)]">
              {moment(solution.createdAt).format("DD MMM 'YY")}
            </span>
          </div>
        </div>

        {!solution.userReport && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                className="cursor-pointer flex items-center justify-center h-8 w-8 rounded-full border border-[var(--q3-stroke-light)]"
              >
                <DotsThreeIcon size={18} />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="p-0 border-none shadow-none bg-transparent min-w-0">
              <DropdownMenuItem
                onClick={() => {
                  // Defer opening the report modal until after Radix has torn
                  // down this dropdown's DismissableLayer - see
                  // MySolutionListItem.jsx for why opening a Dialog
                  // synchronously from a dropdown item's click can leave the
                  // page stuck unclickable after the modal closes.
                  setTimeout(() => onReport(solution._id), 0);
                }}
                className="cursor-pointer text-sm text-[var(--q3-neutral-default)] bg-[var(--q3-surface-default)] border border-[var(--q3-stroke-light)] shadow-md rounded-full flex items-center justify-center gap-1 px-3 py-1.5"
              >
                <FlagIcon size={16} className="text-[var(--q3-danger-normal)]" /> Report error
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>

      <SolutionImage images={solution?.solution?.images} />

      <div className="flex flex-row items-center gap-2">
        <button
          type="button"
          onClick={() => handleVote('upvote')}
          disabled={isVoting}
          className={`cursor-pointer flex items-center gap-1 whitespace-nowrap shrink-0 text-xs md:text-sm font-medium border rounded-full px-3 py-1.5 ${
            myVote === 'upvote'
              ? 'border-[var(--q3-accent-normal)] text-[var(--q3-accent-normal)] bg-[var(--q3-accent-light)]'
              : 'border-[var(--q3-stroke-light)] text-[var(--q3-neutral-default)]'
          }`}
        >
          <HandsClappingIcon className="text-sm md:text-base" weight={myVote === 'upvote' ? 'fill' : 'regular'} />
          Appreciate • {localUpvotes}
        </button>
        <button
          type="button"
          onClick={() => handleVote('downvote')}
          disabled={isVoting}
          className={`cursor-pointer flex items-center justify-center text-xs md:text-sm h-8 w-8 rounded-full border shrink-0 ${
            myVote === 'downvote'
              ? 'border-[var(--q3-danger-normal)] text-[var(--q3-danger-normal)]'
              : 'border-[var(--q3-stroke-light)] text-[var(--q3-neutral-default)]'
          }`}
        >
          <ThumbsDownIcon className="text-sm md:text-base" weight={myVote === 'downvote' ? 'fill' : 'regular'} />
        </button>
      </div>
    </div>
  );
};

export default OtherSolutionCard;
